import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export function useMyClubStaff() {
  const { user } = useAuth();

  const { data: myStaffRoles, isLoading } = useQuery({
    queryKey: ['my-staff-roles', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('club_staff')
        .select('*, organization:organizations(id, name, type, logo_url)')
        .eq('user_id', user.id);

      if (error) throw error;
      return data || [];
    },
    enabled: !!user,
  });

  const isClubStaff = (organizationId?: string) => {
    if (!organizationId) return false;
    return myStaffRoles?.some(sr => sr.organization_id === organizationId) || false;
  };

  const isClubAdmin = (organizationId?: string) => {
    if (!organizationId) return false;
    return myStaffRoles?.some(sr => 
      sr.organization_id === organizationId && sr.role === 'admin'
    ) || false;
  };

  const getFirstOrganizationId = (): string | null => {
    return myStaffRoles?.[0]?.organization_id || null;
  };

  const getFirstClubOrganization = () => {
    return myStaffRoles?.find(sr => sr.organization?.type === 'club') || null;
  };

  const getFirstAssociationOrganization = () => {
    return myStaffRoles?.find(sr => sr.organization?.type === 'association') || null;
  };

  const hasPermission = (organizationId: string, permission: string): boolean => {
    const staff = myStaffRoles?.find(sr => sr.organization_id === organizationId);
    if (!staff) return false;
    if (staff.role === 'admin') return true;
    
    const permissions = staff.permissions as Record<string, boolean> || {};
    return permissions[permission] === true;
  };

  return {
    myStaffRoles: myStaffRoles || [],
    isLoading,
    isClubStaff,
    isClubAdmin,
    getFirstOrganizationId,
    getFirstClubOrganization,
    getFirstAssociationOrganization,
    hasPermission,
  };
}
